import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Badge } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import UpdateRoomModule from './UpdateRoomModule';

interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data: T;
}

interface RoomDTO {
  id: number;
  name: string;
  floorId: number;
  buildingId: number;
  status: 'ACTIVE' | 'INACTIVE' | 'ONGOING_REPAIR' | string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  type?: string;
  description?: string;
  doors?: any[];
}

interface DoorDTO {
  id: number;
  name: string;
  roomId: number;
  floorId: number;
  buildingId: number;
  doorType?: string | null;
  description?: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

interface KeyDTO {
  id: string;
  keyId: string;
  type: string;
  noOfKeys: number;
  roomId?: number;
}

interface GetKeysResponse {
  keys: KeyDTO[];
  total: number;
}

interface ViewRoomModuleProps {
  show: boolean;
  onHide: () => void;
  onUpdated?: () => void;
  response?: ApiResponse<RoomDTO> | null;
  room?: RoomDTO | null;
}

const statusVariant = (s: string) => {
  switch (s) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'secondary';
    case 'ONGOING_REPAIR':
      return 'warning';
    default:
      return 'info';
  }
};

const formatDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '-');

const ViewRoomModule: React.FC<ViewRoomModuleProps> = ({
  show,
  onHide,
  onUpdated,
  response,
  room,
}) => {
  const rawRoom = response?.data ?? room ?? null;
  const [showUpdate, setShowUpdate] = useState(false);
  const [doorCount, setDoorCount] = useState<number | null>(null);
  const [keyCount, setKeyCount] = useState<number | null>(null);
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  // Fetch door and key counts when modal is shown
  useEffect(() => {
    const fetchCounts = async () => {
      if (!rawRoom || !show) return;
      if (!checkPermission('ROLE_VIEW_DOOR') || !checkPermission('ROLE_GET_KEYS')) {
        showError('You do not have permission to view doors or keys.');
        return;
      }
      setIsLoadingCounts(true);
      try {
        const [doorRes, keyRes] = await Promise.all([
          apiDefinitions.getDoorsByRoomId(rawRoom.id),
          apiDefinitions.getKeysByRoomId(rawRoom.id),
        ]);

        // Door count
        if (doorRes.status === 200) {
          const doorData: DoorDTO[] = doorRes.data.data || [];
          setDoorCount(doorData.length);
        } else {
          showError(doorRes.data?.statusDescription || 'Failed to fetch door count');
          setDoorCount(0);
        }

        // Key count
        if (keyRes.status === 200) {
          const keyData: GetKeysResponse = keyRes.data.data;
          setKeyCount(keyData.total || 0);
        } else {
          showError(keyRes.data?.statusDescription || 'Failed to fetch key count');
          setKeyCount(0);
        }
      } catch (e: any) {
        showError('Failed to fetch door or key counts. Please try again.');
        setDoorCount(0);
        setKeyCount(0);
      } finally {
        setIsLoadingCounts(false);
      }
    };
    fetchCounts();
  }, [rawRoom, show]);

  if (!rawRoom) {
    return (
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex justify-content-between w-100 align-items-center">
            <span>
              View Room: {rawRoom.name}{' '}
              <Badge bg={statusVariant(rawRoom.status)} className="ms-2">
                {rawRoom.status}
              </Badge>
            </span>
            <div className="d-flex gap-2">
              {isLoadingCounts ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Badge
                    bg="secondary"
                    className="rounded-pill"
                    title={`${doorCount ?? rawRoom.doors?.length ?? 0} door(s)`}
                  >
                    <i className="ti ti-door me-1"></i>
                    {doorCount ?? rawRoom.doors?.length ?? '-'}
                  </Badge>
                  <Badge bg="dark" className="rounded-pill" title={`${keyCount || 0} key(s)`}>
                    <i className="ti ti-key me-1"></i>{keyCount !== null ? keyCount : '-'}
                  </Badge>
                </>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* First row - ID, Name, Status */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="roomId">
                  <Form.Label>ID</Form.Label>
                  <Form.Control type="text" value={rawRoom.id} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="roomName">
                  <Form.Label>Name</Form.Label>
                  <Form.Control type="text" value={rawRoom.name} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="status">
                  <Form.Label>Status</Form.Label>
                  <Form.Control type="text" value={rawRoom.status} disabled />
                </Form.Group>
              </Col>
            </Row>

            {/* Second row - Type, Floor ID, Building ID */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="floorId">
                  <Form.Label>Floor ID</Form.Label>
                  <Form.Control type="text" value={rawRoom.floorId} disabled />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="buildingId">
                  <Form.Label>Building ID</Form.Label>
                  <Form.Control type="text" value={rawRoom.buildingId} disabled />
                </Form.Group>
              </Col>
            </Row>
            {/* Description full width */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group controlId="description">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={rawRoom.description ?? '-'}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Audit info - Created/Updated */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group controlId="createdBy">
                  <Form.Label>Created By</Form.Label>
                  <Form.Control type="text" value={rawRoom.createdBy} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="updatedBy">
                  <Form.Label>Updated By</Form.Label>
                  <Form.Control type="text" value={rawRoom.updatedBy ?? '-'} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="createdAt">
                  <Form.Label>Created At</Form.Label>
                  <Form.Control type="text" value={formatDateTime(rawRoom.createdAt)} disabled />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group controlId="updatedAt">
                  <Form.Label>Updated At</Form.Label>
                  <Form.Control type="text" value={formatDateTime(rawRoom.updatedAt)} disabled />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" className="me-2" onClick={() => setShowUpdate(true)}>
                Update
              </Button>
              <Button variant="primary" onClick={onHide}>
                Close
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Update modal */}
      <UpdateRoomModule
        show={showUpdate}
        onHide={() => setShowUpdate(false)}
        room={rawRoom}
        onSuccess={() => {
          setShowUpdate(false);
          onUpdated?.();
        }}
      />
    </>
  );
};

export default ViewRoomModule;